import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Quote } from "lucide-react";
import Marquee from "react-fast-marquee";
import { useTranslation } from "react-i18next";
import { OrbitalBackdrop, RotatingRing } from "@/components/brand/Sigils";
import { ArmGlyph, IyafWordmark } from "@/components/brand/IyafMark";
import InstagramFeed from "@/components/site/InstagramFeed";
import { AnimatedCounter } from "@/components/ui-iyaf/AnimatedCounter";
import { RegisterSoonButton } from "@/components/ui-iyaf/RegisterSoonButton";
import { arms, heroStats, partnersMarquee, stories } from "@/data/content";

export default function Home() {
  const { t } = useTranslation();
  const featuredStories = stories.filter((s) => ["huggsies", "nur", "misto-chocolate", "aiesec-jordan"].includes(s.slug));

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative pt-12 pb-28 sm:pt-20 sm:pb-32 overflow-hidden" data-testid="home-hero">
        <OrbitalBackdrop />
        <div className="container-iyaf relative">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow text-iyaf-blue mb-6">
            {t("home.eyebrow")}
          </motion.p>

          <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }} className="font-display tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-[80px] max-w-5xl">
            {t("home.heroLine1")}
            <br />
            {t("home.heroLine2")}
            <br />
            <span className="font-editorial italic font-normal text-iyaf-blue">{t("home.heroLine3")}</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="mt-8 font-editorial text-2xl sm:text-3xl text-iyaf-ink/75 leading-snug max-w-3xl">
            {t("home.heroBody")}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }} className="mt-10 flex flex-wrap gap-3">
            <RegisterSoonButton
              label={t("cta.registerInterest")}
              icon={ArrowUpRight}
              testid="hero-cta-register"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-iyaf-ink text-white font-semibold hover:bg-iyaf-blue transition-colors"
            />
            <Link to="/model" data-testid="hero-cta-model" className="inline-flex items-center gap-2 px-7 py-4 rounded-full border-2 border-iyaf-ink font-semibold hover:bg-iyaf-ink hover:text-white transition-colors">
              {t("cta.seeHowTheModelWorks")}
            </Link>
            <Link to="/engage" data-testid="hero-cta-partner" className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-iyaf-ink/70 hover:text-iyaf-ink font-semibold">
              {t("cta.partnerWithUs")} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>

        {/* Metric strip (CPF-style under hero) */}
        <div className="container-iyaf relative mt-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 rounded-3xl overflow-hidden border border-black/5">
            {heroStats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-white p-6 sm:p-8"
                data-testid={`stat-${s.key}`}
              >
                <p className="eyebrow text-iyaf-ink/45">{t(`homeData.stats.${s.key}`)}</p>
                <p className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3 text-iyaf-ink">
                  <AnimatedCounter value={s.value} />
                </p>
                <p className="text-xs text-iyaf-ink/55 mt-2">{t(`homeData.statNotes.${s.key}`, s.note)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* THE 97/3 MODEL — signature methodology */}
      <section className="relative py-24 bg-iyaf-ink text-white overflow-hidden" data-testid="home-97-3">
        <div className="pointer-events-none absolute -right-32 top-10 w-[520px] h-[520px] rounded-full border border-white/8 animate-spin-slow" />
        <div className="container-iyaf relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <p className="eyebrow text-iyaf-orange mb-5">{t("home.modelEyebrow")}</p>
              <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.02] text-balance">
                {t("home.modelTitle")}
              </h2>
              <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md">
                {t("home.modelBody")}
              </p>
              <Link to="/iyaf-social" className="mt-8 inline-flex items-center gap-2 text-iyaf-orange font-semibold">
                {t("homeData.insideArm", { arm: "IYAF Social" })} <ArrowRight size={16} />
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="relative aspect-[5/4] sm:aspect-[6/4] rounded-3xl bg-white/[0.04] border border-white/10 p-8 sm:p-12 overflow-hidden">
                {/* Funnel diagram */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-full max-w-md">
                    <div className="flex flex-col gap-3">
                      <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="rounded-full bg-white/8 border border-white/15 px-6 py-4 flex items-center justify-between">
                        <span className="text-sm text-white/70">{t("homeData.funnel.assessed")}</span>
                        <span className="font-display text-2xl font-bold">100%</span>
                      </motion.div>
                      <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="mx-6 rounded-full bg-iyaf-gray/30 border border-white/15 px-6 py-4 flex items-center justify-between">
                        <span className="text-sm text-white/70">{t("homeData.funnel.redirected")}</span>
                        <span className="font-display text-2xl font-bold text-iyaf-gray">97</span>
                      </motion.div>
                      <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="mx-16 rounded-full bg-iyaf-orange text-iyaf-ink px-6 py-4 flex items-center justify-between font-semibold">
                        <span className="text-sm">{t("homeData.funnel.intensive")}</span>
                        <span className="font-display text-2xl font-bold">3</span>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE FOUR ARMS */}
      <section className="relative py-28 overflow-hidden">
        <RotatingRing size={480} className="absolute -left-40 top-32 opacity-25" />
        <div className="container-iyaf relative">
          <div className="max-w-2xl mb-16">
            <p className="eyebrow text-iyaf-blue mb-4">{t("home.armsEyebrow")}</p>
            <h2 className="h2 text-balance">{t("home.armsTitle")}</h2>
            <p className="mt-4 font-editorial text-2xl text-iyaf-ink/70 leading-snug max-w-xl">
              {t("home.armsBody")}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {arms.map((arm, i) => (
              <motion.div
                key={arm.key}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group relative p-8 sm:p-10 rounded-3xl bg-white border border-black/5 hover:border-transparent transition-all overflow-hidden"
                data-testid={`arm-card-${arm.key}`}
              >
                <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full opacity-[0.06] group-hover:opacity-100 transition-opacity duration-700" style={{ background: arm.color }} />
                <div className="relative mb-5 flex items-center justify-between gap-3">
                  <IyafWordmark variant={arm.key} size="sm" />
                  <span className="small-caps text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: `${arm.color}1A`, color: arm.color }}>
                    {arm.key === "venture" ? t("armCards.launching", { year: 2027 }) : t("armCards.active")}
                  </span>
                </div>
                <p className="text-sm text-iyaf-ink/60 mb-3">{t(`armCards.${arm.key}.tagline`)}</p>
                <p className="text-iyaf-ink/75 leading-relaxed mb-5">{t(`armCards.${arm.key}.short`)}</p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  {arm.metrics.slice(0, 4).map((m, idx) => (
                    <div key={m.k} className="p-3 rounded-xl bg-iyaf-paper border border-black/5">
                      <p className="font-display text-xl font-bold" style={{ color: arm.color }}>{m.v}</p>
                      <p className="text-[10px] tracking-wider uppercase text-iyaf-ink/50">{t(`armCards.${arm.key}.metrics.m${idx + 1}`)}</p>
                    </div>
                  ))}
                </div>

                <Link to={arm.href} className="inline-flex items-center gap-2 font-semibold text-sm" style={{ color: arm.color }}>
                  {t("homeData.exploreArm", { arm: arm.name })} <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SUCCESS STORIES PREVIEW */}
      <section className="py-28 bg-white border-y border-black/5">
        <div className="container-iyaf">
          <div className="flex flex-col lg:flex-row gap-8 lg:items-end justify-between mb-12">
            <div className="max-w-2xl">
              <p className="eyebrow text-iyaf-orange mb-4">{t("home.storiesEyebrow")}</p>
              <h2 className="h2 text-balance">{t("home.storiesTitle")}</h2>
              <p className="mt-4 font-editorial text-2xl text-iyaf-ink/70 leading-snug">
                {t("home.storiesBody")}
              </p>
            </div>
            <Link to="/stories" className="inline-flex items-center gap-2 font-semibold">
              {t("homeExtras.allStoriesCta")} <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {featuredStories.map((s, i) => (
                <motion.article
                  key={s.slug}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.55, delay: i * 0.06 }}
                  className="group"
                  data-testid={`story-${s.slug}`}
                >
                  <div className="relative overflow-hidden rounded-[28px] aspect-[4/5] mb-4 bg-iyaf-paper">
                    <img src={s.image} alt={s.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.armColor }} />
                    <span className="small-caps text-[10px] text-iyaf-ink/55">{t(`homeData.storyCategory.${s.arm}`, s.arm)}</span>
                  </div>
                  <h3 className="font-display font-black text-xl leading-tight">{s.name}</h3>
                  <p className="text-sm text-iyaf-ink/65 mt-2 line-clamp-2">{s.summary}</p>
                </motion.article>
              ))}
          </div>
        </div>
      </section>

      {/* SUSTAINABILITY HIGHLIGHT */}
      <section className="py-28">
        <div className="container-iyaf">
          <div className="relative rounded-[40px] sm:rounded-[60px] bg-iyaf-paper border border-black/5 p-10 sm:p-16 overflow-hidden">
            <div className="absolute -right-32 -bottom-32 w-[420px] h-[420px] rounded-full border border-iyaf-blue/15 animate-spin-slow" />
            <p className="eyebrow text-iyaf-blue mb-5">{t("home.sustainabilityEyebrow")}</p>
            <h2 className="h2 max-w-3xl text-balance">{t("home.sustainabilityTitle")}</h2>
            <div className="mt-12 grid sm:grid-cols-3 gap-8">
              <div>
                <p className="font-display text-7xl font-extrabold text-iyaf-blue leading-none">
                  <AnimatedCounter value="70%" />
                </p>
                <p className="mt-3 font-semibold">{t("homeData.sustain.selfLabel")}</p>
                <p className="text-sm text-iyaf-ink/60 mt-1">{t("homeData.sustain.selfDesc")}</p>
              </div>
              <div>
                <p className="font-display text-7xl font-extrabold text-iyaf-orange leading-none">
                  <AnimatedCounter value="12" /><span className="text-3xl ml-1">mo.</span>
                </p>
                <p className="mt-3 font-semibold">{t("homeData.sustain.reserveLabel")}</p>
                <p className="text-sm text-iyaf-ink/60 mt-1">{t("homeData.sustain.reserveDesc")}</p>
              </div>
              <div>
                <p className="font-display text-7xl font-extrabold text-iyaf-magenta leading-none">
                  <AnimatedCounter value="100%" />
                </p>
                <p className="mt-3 font-semibold">{t("homeData.sustain.profitLabel")}</p>
                <p className="text-sm text-iyaf-ink/60 mt-1">{t("homeData.sustain.profitDesc")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS MARQUEE */}
      <section className="py-20 bg-iyaf-ink text-white">
        <div className="container-iyaf mb-8 flex justify-between items-end">
          <div>
            <p className="eyebrow text-white/40 mb-4">{t("home.partnersEyebrow")}</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">{t("homeExtras.partnersHeadline")}</h2>
          </div>
          <Link to="/impact" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold hover:text-iyaf-orange">
            {t("homeExtras.allPartnersCta")} <ArrowRight size={16} />
          </Link>
        </div>
        <div className="marquee-mask">
          <Marquee gradient={false} speed={36}>
            {partnersMarquee.concat(partnersMarquee).map((p, i) => (
              <span key={i} className="mx-10 font-display text-2xl sm:text-3xl text-white/55 hover:text-white transition-colors whitespace-nowrap">{p}</span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* INSTAGRAM FEED */}
      <InstagramFeed />

      {/* CLOSING CTA */}
      <section className="py-28">
        <div className="container-iyaf">
          <div className="relative rounded-[40px] bg-iyaf-blue overflow-hidden p-10 sm:p-16 text-white">
            <div className="pointer-events-none absolute -right-20 -top-20 w-80 h-80 rounded-full border border-white/20 animate-spin-slow" />
            <div className="pointer-events-none absolute right-20 -bottom-32 w-96 h-96 rounded-full border border-white/15" />
            <IyafWordmark variant="main" size="lg" inverse className="mb-6 relative" />
            <h2 className="relative font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl text-balance">
              {t("home.ctaTitle")}
            </h2>
            <div className="relative mt-10 flex flex-wrap gap-4">
              <RegisterSoonButton
                label={t("cta.registerInterest")}
                testid="footer-cta-register"
                className="px-7 py-4 rounded-full bg-white text-iyaf-ink font-semibold hover:bg-iyaf-orange hover:text-iyaf-ink transition"
              />
              <Link to="/contact" className="px-7 py-4 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-iyaf-ink transition">
                {t("cta.partnerWithUs")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
